import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, ClipboardEdit, RefreshCw } from 'lucide-react';
import API_BASE from '../../utils/apiBase';

/** A manager-only, tenant-scoped review of amendments to approved work history. */
export default function EmploymentCorrectionInbox({ employeeId, token, onReviewed }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [notes, setNotes] = useState({});
  const refresh = useCallback(async () => {
    if (!employeeId || !token) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE}/employees/${employeeId}/employment-history/correction-requests`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPending(response.data?.requests || []);
      setError('');
    } catch (_err) {
      setError('We could not load correction requests. Refresh before reviewing this history.');
    } finally {
      setLoading(false);
    }
  }, [employeeId, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const decide = async (item, approve) => {
    if (activeId || String(notes[item.id] || '').trim().length < 5) return;
    setActiveId(item.id);
    setError('');
    try {
      await axios.post(
        `${API_BASE}/employees/${employeeId}/employment-history/correction-requests/${item.id}/decide`,
        { approve, notes: notes[item.id].trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await refresh();
      if (onReviewed) await onReviewed();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not review this correction. Refresh and try again.');
    } finally {
      setActiveId(null);
    }
  };

  if (!pending.length && !error && !loading) return null;
  return (
    <section className="rounded-xl border border-amber-200 bg-white p-4 space-y-3"
      data-testid="admin-work-history-corrections">
      <header className="flex items-center justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <ClipboardEdit className="h-4 w-4" /> Work history corrections
          </h3>
          <p className="text-xs text-slate-600">Review proposed changes before reopening approved history.</p>
        </div>
        <button type="button" onClick={refresh} className="min-h-10 px-3 text-sm text-teal-800"
          aria-label="Refresh work history corrections"><RefreshCw className="h-4 w-4" /></button>
      </header>
      {loading && <p className="text-xs text-slate-600">Checking requests…</p>}
      {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
      {pending.map(item => (
        <div key={item.id} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="pending-work-history-correction">
          <p className="text-sm font-semibold">
            {item.entry_id ? 'Change existing role' : 'Add another role'}: {item.proposed_entry?.employer_name || 'Employer'}
          </p>
          <p className="text-xs text-slate-600">{item.reason}</p>
          {item.original_entry && (
            <p className="text-xs text-slate-700">
              Previous: {item.original_entry.employer_name} · {item.original_entry.job_title} · {item.original_entry.start_date} — {item.original_entry.end_date || 'Present'}
            </p>
          )}
          <p className="text-xs text-slate-700">
            Proposed: {item.proposed_entry?.employer_name} · {item.proposed_entry?.job_title} · {item.proposed_entry?.start_date} — {item.proposed_entry?.end_date || 'Present'}
          </p>
          <label className="block text-xs font-medium text-slate-700">
            Review note
            <textarea value={notes[item.id] || ''} onChange={e => setNotes(old => ({ ...old, [item.id]: e.target.value }))}
              placeholder="Reason for your decision" rows={2}
              className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm" />
          </label>
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={Boolean(activeId) || String(notes[item.id] || '').trim().length < 5}
              className="min-h-11 rounded-lg bg-teal-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              onClick={() => decide(item, true)}>
              <CheckCircle2 className="mr-1 inline h-4 w-4" /> Accept correction
            </button>
            <button type="button" disabled={Boolean(activeId) || String(notes[item.id] || '').trim().length < 5}
              className="min-h-11 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold disabled:opacity-50"
              onClick={() => decide(item, false)}>Reject</button>
          </div>
          <p className="text-xs text-slate-500">Accepted corrections reopen review. They do not inherit prior verification.</p>
        </div>
      ))}
    </section>
  );
}
